import React, { useState, useEffect } from 'react';
import Axios from 'axios';
import { useNavigate, useLocation } from 'react-router-dom';
import { toast, ToastContainer } from 'react-toastify';
import UserProfileHeader from './UserProfileHeader';
import "../Styles/CustomerManagement.css";
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogTitle from '@mui/material/DialogTitle';

const UserManagement = () => {
    const [users, setUsers] = useState([]);
    const [selectedUsers, setSelectedUsers] = useState([]);
    const [selectAll, setSelectAll] = useState(false);
    const [openDialog, setOpenDialog] = useState(false);
    const [originalUsers, setOriginalUsers] = useState([]);
    const [isDirty, setIsDirty] = useState(false);
    const navigate = useNavigate();
    const location = useLocation();
    const role = localStorage.getItem('role');

    const roleMap = {
        1: 'MD Admin',
        2: 'MD Tech',
        3: 'SI Admin',
        4: 'SI Tech'
    };

    const userStatusMap = {
        1: 'Pending Approval',
        2: 'Pending Activation',
        3: 'Activated',
        4: 'No Key Assigned'
    };

    const reverseStatusMap = Object.fromEntries(
        Object.entries(userStatusMap).map(([k, v]) => [v, Number(k)])
    );

    const formatFetchedUsers = (users) => {
        return users.map(user => ({
            ...user,
            role: roleMap[user.role] || user.role,
            status: userStatusMap[user.status] || user.status
        }));
    };

    const statusesChanged = (current, original) => {
        if (current.length !== original.length) return true;
        const byEmail = Object.fromEntries(original.map(u => [u.email, u.status]));
        return current.some(u => byEmail[u.email] !== u.status);
    };

    const fetchUsers = () => {
        Axios.post(`/get_customers`).then((response) => {
            if (response.data.success) {
                const formatted = formatFetchedUsers(response.data.data);
                setUsers(formatted);
                setOriginalUsers(formatted);
                setIsDirty(false);
            } else {
                toast.error("Failed to get all users.", {
                    position: "top-right",
                    autoClose: 5000,
                    hideProgressBar: false,
                    closeOnClick: true,
                    pauseOnHover: true,
                    draggable: true,
                    progress: undefined,
                    theme: "light",
                });
            }
        });
    };

    useEffect(() => {
        fetchUsers();
    }, []);

    const handleSelectUser = (userEmail) => {
        setSelectedUsers((prev) => {
            if (prev.includes(userEmail)) {
                return prev.filter((email) => email !== userEmail);
            } else {
                return [...prev, userEmail];
            }
        });
    };

    const handleSelectAll = () => {
        if (selectAll) {
            setSelectedUsers([]);
        } else {
            setSelectedUsers(users.map(user => user.email));
        }
        setSelectAll(!selectAll);
    };

    const handleDeleteUsers = () => {
        Axios.post(`/delete_users`, { emails: selectedUsers }).then((response) => {
            if (response.data.success) {
                toast.success(`${response.data.msg}`, {
                    position: "top-right",
                    autoClose: 5000,
                    hideProgressBar: false,
                    closeOnClick: true,
                    pauseOnHover: true,
                    draggable: true,
                    progress: undefined,
                    theme: "light",
                });
            } else {
                toast.error(`${response.data.msg}`, {
                    position: "top-right",
                    autoClose: 5000,
                    hideProgressBar: false,
                    closeOnClick: true,
                    pauseOnHover: true,
                    draggable: true,
                    progress: undefined,
                    theme: "light",
                });
            }
            fetchUsers();
            setSelectedUsers([]);
            setOpenDialog(false);
        });
    };

    const handleStatusChange = (userEmail, newStatus) => {
        setUsers((prevUsers) => {
            const next = prevUsers.map(u =>
                u.email === userEmail ? { ...u, status: newStatus } : u
            );
            setIsDirty(statusesChanged(next, originalUsers));
            return next;
        });
    };

    const handleSave = async () => {
        try {
            // 构建 payload：email + statusId
            const payload = users.map(u => ({
                email: u.email,
                status: reverseStatusMap[u.status] ?? u.status
            }));

            const res = await Axios.post('/update_customer_statuses', { customers: payload, operator_email: (localStorage.getItem("userEmail") || "").trim() });
            if (res.data?.success) {
                toast.success(res.data.msg || 'Status saved.', { position: 'top-right' });
                setOriginalUsers(users);
                setIsDirty(false);
            } else {
                toast.error(res.data?.msg || 'Failed to save status.', { position: 'top-right' });
            }
        } catch (err) {
            toast.error('Network or server error while saving.', { position: 'top-right' });
            console.error(err);
        }
    };

    const handleBack = () => {
        console.log("Back button clicked!");
        console.log("Current path:", location.pathname);
        // Dismiss any active toasts before navigating
        toast.dismiss();
        // Check if we can go back in history
        if (window.history.state && window.history.state.idx > 0) {
            navigate(-1);  // Go back one step in history
            console.log("Navigating back one step.");
        } else {
            // If no history, navigate to the dashboard without adding an entry
            navigate('/dashboard', { replace: true });
            console.log("No history to go back, redirecting to dashboard.");
        }
    };

    return (
        <div className="dashboard">
            <UserProfileHeader isAdmin={true} />

            <div className="all-users-card">
                <h1 className="Title-alluser">Customer List</h1>

                <div className="button-row">
                    <button onClick={fetchUsers} className="refresh-button">Refresh</button>
                    <button onClick={handleSave} className="save-button" disabled={!isDirty}>Save</button>
                    <button
                        onClick={() => setOpenDialog(true)}
                        className="delete-button"
                        disabled={selectedUsers.length === 0}
                    >
                        Delete
                    </button>
                </div>

                <div className="table-container">
                    <table className="styled-table">
                        <thead>
                            <tr>
                                <th>
                                    <input
                                        type="checkbox"
                                        checked={selectAll}
                                        onChange={handleSelectAll}
                                    />
                                </th>
                                <th>Order</th>
                                <th>First Name</th>
                                <th>Last Name</th>
                                <th>Email</th>
                                <th>Company Name</th>
                                <th>MD ID</th>
                                <th>SI ID</th>
                                <th>Customer ID</th>
                                <th>YubiKey Serial Number</th>
                                <th>Role</th>
                                <th>Status</th>
                            </tr>
                        </thead>
                        <tbody>
                            {users.map((user, index) => (
                                <tr key={user.email}>
                                    <td>
                                        <input
                                            type="checkbox"
                                            checked={selectedUsers.includes(user.email)}
                                            onChange={() => handleSelectUser(user.email)}
                                        />
                                    </td>
                                    <td>{index + 1}</td>
                                    <td>{user.first_name}</td>
                                    <td>{user.last_name}</td>
                                    <td>{user.email}</td>
                                    <td>{user.company_name}</td>
                                    <td>{user.md_id || "-"}</td>
                                    <td>{user.si_id || "-"}</td>
                                    <td>{user.customer_id || "-"}</td>
                                    <td>{user.key_serial_number || "-"}</td>
                                    <td>{user.role}</td>
                                    <td>
                                        {(() => {
                                            const originalUser = originalUsers.find(u => u.email === user.email);
                                            const originalStatus = originalUser ? originalUser.status : user.status;
                                            const originallyNoKey = originalStatus === 'No Key Assigned';
                                            if (role != 1 || originallyNoKey) {
                                                return <span className="status-readonly">{user.status}</span>;
                                            }
                                            return (
                                                <select
                                                    className="role-dropdown"
                                                    value={user.status}
                                                    onChange={(e) =>
                                                        handleStatusChange(user.email, e.target.value)
                                                    }
                                                >
                                                    <option value={originalStatus}>{originalStatus}</option>
                                                    {originalStatus !== 'No Key Assigned' && (
                                                        <option value="No Key Assigned">No Key Assigned</option>
                                                    )}
                                                </select>
                                            );
                                        })()}
                                    </td>

                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>

                <Dialog
                    open={openDialog}
                    onClose={() => setOpenDialog(false)}
                    aria-labelledby="alert-dialog-title"
                    aria-describedby="alert-dialog-description"
                >
                    <DialogTitle id="alert-dialog-title">{"Delete Users"}</DialogTitle>
                    <DialogContent>
                        <DialogContentText id="alert-dialog-description">
                            Do you want to delete these users?
                        </DialogContentText>
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={handleDeleteUsers} color="primary">Yes</Button>
                        <Button onClick={() => setOpenDialog(false)} color="secondary" autoFocus>No</Button>
                    </DialogActions>
                </Dialog>
            </div>

            <ToastContainer />
        </div>
    );
};

export default UserManagement;
